import { Bars2Icon, QueueListIcon } from "@heroicons/react/24/outline";
import { useState } from "react";

export type RowData = {
  [key: string]: string | number | JSX.Element;
};

export type GridCol = {
  field: string;
  headerName: string | JSX.Element;
  type?: "number" | "string";
  style: string;
};

export type GridRow = {
  rowCkickHandler?: () => void | Promise<void>;
  rowData: RowData;
  style?: string;
  draggable?: boolean;
};

export type DraggableTableProps = {
  gridCol: GridCol[];
  gridRow: GridRow[];
  height?: string;
  dragEndHandle: (e: any) => void;
}

const DraggableTable = ({
  gridCol, gridRow, height= "", dragEndHandle
}: DraggableTableProps) => {

  const [gridRowList, setGridRowList] = useState<GridRow[]>(gridRow);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const dragStart = (index: number) => {
    setDragIndex(index);
  };

  const dragEnter = (index: number) => {
    if (index === dragIndex) return;
    setGridRowList((prevState) => {
      let newGridRowList: any = [...prevState];
      const deleteElement = newGridRowList.splice(dragIndex, 1)[0];
      newGridRowList.splice(index, 0, deleteElement);
      return newGridRowList;
    });
    setDragIndex(index);
  };

  const dragEnd = () => {
    dragEndHandle(gridRowList);
    setDragIndex(null);
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full p-1 align-middle">
            <div className="relative overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full table-fixed divide-y divide-gray-300">
                <thead className="bg-gray-50 ">
                  <tr>
                    <th className="flex justify-center whitespace-nowrap relative py-3.5">
                      <QueueListIcon className={"shrink-0 h-4 w-4"}/>
                    </th>
                    <th className="whitespace-nowrap px-3 relative py-3.5 text-left text-sm font-semibold">
                      No.
                    </th>
                    {gridCol.map((item, index) => {
                      return (
                        <th
                          scope="col"
                          className={`whitespace-nowrap px-3 relative py-3.5 text-left text-sm font-semibold ${item.style}`}
                          key={`th-${index}`}
                        >
                          {item.headerName}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {gridRowList.map((item, index) => {
                    return (
                      <tr
                        key={`tr-${index}`}
                        className={`${index % 2 === 0 ? undefined : "bg-gray-50"}`}
                        draggable={item.draggable ?? true}
                        onDragStart={item.draggable ?? true ? () => dragStart(index): () => {}}
                        onDragEnter={item.draggable ?? true ? () => dragEnter(index): () => {}}
                        onDragOver={item.draggable ?? true ? (e) => e.preventDefault(): () => {}}
                        onDragEnd={item.draggable ?? true ? dragEnd: () => {}}
                      >
                        <td
                          className={`whitespace-nowra px-3 py-4 text-sm text-gray-500 ${item.style}`}
                          key={`td-${index}-arrow`}
                        >
                          {(item.draggable ?? true) ? (
                            <div className="flex justify-center cursor-grab active:cursor-grabbing">
                              <Bars2Icon className={"shrink-0 h-4 w-4"}/>
                            </div>
                          ) : (<></>)}
                        </td>
                        <td
                          className={`whitespace-nowra text-left px-3 py-4 text-sm text-gray-500 ${item.style}`}
                          key={`td-${index}-num`}
                        >
                          {index + 1}
                        </td>
                        {gridCol.map((col) => {
                          return (
                            <td
                              className={`whitespace-nowra text-left px-3 py-4 text-sm text-gray-500 ${item.style}`}
                              key={`td-${index}-${col.field}`}
                            >
                              {item.rowData[col.field]}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default DraggableTable;